/**
 * One auth modal for the whole app.
 *
 * The site used to be gated at the router: every route was wrapped in
 * ProtectedRoute and a signed-out visitor was bounced to /auth before seeing
 * anything. The directory is now public, so authentication is no longer a wall
 * in front of the app: it is something that happens over the top of it, when a
 * visitor reaches for something that needs an account.
 *
 * Any component can call `useAuthModal().open()`. The modal itself is rendered
 * once, by this provider, at the root of the tree, so opening it never unmounts
 * the page underneath, and the directory stays visible behind the overlay.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type AuthModalMode = "signup" | "login";

export interface AuthModalOptions {
  /** Which tab to land on. Defaults to signup: most openings are new visitors. */
  mode?: AuthModalMode;
  /** Heading shown above the form. Say why the account is needed. */
  title?: string;
  /** Supporting line under the heading. */
  description?: string;
  /**
   * Where to send them once they are signed in. Omit to stay on the current
   * page, which is what the quiz wants: the modal closes and the result is
   * revealed in place.
   */
  returnTo?: string;
  /**
   * Called after a successful signup or login, before the modal closes.
   * Ignored when `returnTo` is set, since that navigates away.
   */
  onSuccess?: () => void;
  /**
   * The quiz result, when the modal is the gate.
   *
   * The dialog's left panel is normally a two minute overview video, which is
   * the right thing for someone who arrived cold. It is the wrong thing for
   * someone who has just answered two questions and been told three tools are
   * waiting: at that point the video is asking them to start over. When this
   * is set, the panel shows what they are one click from instead.
   */
  result?: {
    count: number;
    roleLabel: string;
    intentLabel: string;
    /** Tailwind classes for the role chip, from ROLE_CARD_STYLES. */
    chipClass: string;
  };
}

interface AuthModalContextValue {
  isOpen: boolean;
  options: AuthModalOptions;
  open: (options?: AuthModalOptions) => void;
  close: () => void;
}

/* REMOVED 2 Sep 2026, with the video it existed for.
   The dialog used to show an overview video, and Open.Video's script reads
   its queue once per page load, so a player unmounting with the dialog came
   back blank the second time. The fix was to keep the player parked
   off-screen on this provider and lend the node to the dialog while it was
   open. Caren asked for the video to go; the parking node went with it.
   If a video ever comes back here, that is the problem to expect, and this
   is the shape of the answer - forceMount is not, because it holds Radix's
   scroll lock open and leaves pointer-events:none on <html>. */

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AuthModalOptions>({});
  const open = useCallback((next: AuthModalOptions = {}) => {
    setOptions(next);
    setIsOpen(true);
  }, []);

  // Options are deliberately left in place on close. Clearing them here would
  // blank the modal's heading during the close animation.
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, options, open, close }),
    [isOpen, options, open, close]
  );

  return (
    <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>
  );
}

export function useAuthModal(): AuthModalContextValue {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error("useAuthModal must be used inside an AuthModalProvider");
  }
  return context;
}
